import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Linguette } from '../componenti/base.tsx'
import { IconaLucchetto, IconaPiu } from '../componenti/Icone.tsx'
import { AnagraficaScheda, IntestazioneScheda } from './SchedaAnagrafica.tsx'
import { sezioniMancanti, type SchedaLetta, type SezioneNonRegistrata } from '../servizi/regole-anagrafica.ts'
import { AvvisoSchedaMigrata, ConsensiScheda } from './SchedaConsensi.tsx'
import { azioneModifica, etichettaStatoScheda, STATI_SCHEDA } from '../servizi/dati-mancanti.ts'
import { SezionePercorsi, type Percorso } from './scheda/Percorsi.tsx'
import { Documenti } from './scheda/Documenti.tsx'
import { CompletaScheda, type Sezione as SezioneDaCompletare } from './scheda/CompletaScheda.tsx'
import { VerificaScheda } from './scheda/VerificaScheda.tsx'
import { AreaRiservata } from './scheda/AreaRiservata.tsx'
import { Cronologia } from './scheda/Cronologia.tsx'
import { SezioneMediazioni } from './scheda/Mediazioni.tsx'
import { ModuliProgetto } from './scheda/ModuliProgetto.tsx'
import { SezioneRicontatti } from './scheda/Ricontatti.tsx'
import { AvvisoSchedeSimili, UnisciSchede } from './scheda/UnisciSchede.tsx'
import { ScriviEmail } from './scheda/ScriviEmail.tsx'
import { useRicontattiScheda } from '../componenti/ricontatti.tsx'
import { useRiferimentoMessenger } from './scheda/RiferimentoMessenger.tsx'

/**
 * GET /persone/:id. L'anagrafica della Scheda Rev05 (intestazione, sezioni, vulnus, categorie) e'
 * descritta da SchedaLetta in servizi/regole-anagrafica.ts; qui si aggiungono consensi e percorsi.
 * `codice_storico` e' l'identificativo della scheda nel sistema precedente (ID_SUPREME), es. «A100».
 */
type SchedaPersona = SchedaLetta & {
  percorsi: Percorso[]
  /** Tipo del documento di identita' registrato sulla scheda: e' il tipo proposto per la foto (D-03). */
  tipo_documento?: string | null
}

type Sezione = 'anagrafica' | 'percorsi' | 'mediazioni' | 'documenti' | 'riservata' | 'cronologia'
const SEZIONI: { valore: Sezione; etichetta: string }[] = [
  { valore: 'anagrafica', etichetta: 'Anagrafica' }, { valore: 'percorsi', etichetta: 'Percorsi' },
  // Indicazioni CIDIS del 17/10/2026: le mediazioni programmate, con giorno, ora e mediatore.
  { valore: 'mediazioni', etichetta: 'Mediazioni' },
  { valore: 'documenti', etichetta: 'Documenti' }, { valore: 'riservata', etichetta: 'Area riservata' },
  { valore: 'cronologia', etichetta: 'Cronologia' },
]

/**
 * Guscio della scheda persona: testata, azioni, linguette. Il contenuto di ogni linguetta sta
 * nei componenti di `pagine/scheda/`, cosi' le sezioni si modificano senza toccare questo file.
 */
export function Scheda() {
  const { id = '' } = useParams()
  const location = useLocation()
  const vaiA = useNavigate()
  // Sezioni che la creazione non ha potuto registrare dopo POST /persone (NuovaPersona.tsx). Si
  // copiano al primo disegno e si tolgono dalla cronologia, che sopravvive al ricaricamento: al
  // ricaricamento l'avviso non ricompare. Valgono solo per la scheda appena creata.
  const statoArrivo = location.state as { avvisi?: SezioneNonRegistrata[] } | null
  const [avvisiCreazione, setAvvisiCreazione] = useState(() => ({ id, voci: statoArrivo?.avvisi ?? [] }))
  useEffect(() => {
    if (statoArrivo?.avvisi) vaiA(location.pathname, { replace: true, state: null })
  }, [statoArrivo, location.pathname, vaiA])
  const [scheda, setScheda] = useState<SchedaPersona | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [sezione, setSezione] = useState<Sezione>('anagrafica')
  // Da «Da completare» si arriva con ?completa=1: il percorso guidato si apre subito. Il parametro
  // si toglie, cosi' ricaricando la pagina il percorso non si riapre.
  const [apriCompleta] = useState(() => new URLSearchParams(location.search).get('completa') === '1')
  useEffect(() => {
    if (new URLSearchParams(location.search).has('completa')) vaiA(location.pathname, { replace: true })
  }, [location.search, location.pathname, vaiA])
  // Consensi, firme di terzi e dati anagrafici si sistemano nelle loro sezioni: il percorso porta li'.
  const [ancora, setAncora] = useState<SezioneDaCompletare | null>(null)
  useEffect(() => {
    if (!ancora || sezione !== 'anagrafica') return
    const t = window.setTimeout(() => {
      document.getElementById(ancora)?.scrollIntoView({ block: 'start' })
      setAncora(null)
    }, 50)
    return () => window.clearTimeout(t)
  }, [ancora, sezione])
  // «Unisci con un'altra scheda» (473). Da un avviso o dalla pagina Migrazione si arriva con
  // ?unisci=<scheda da unire>: la finestra si apre con quella scheda, e il parametro si toglie. Chi
  // apre una scheda gia' unita arriva alla scheda tenuta, con un avviso.
  const statoUnita = location.state as { daSchedaUnita?: boolean } | null
  const [unione, setUnione] = useState<{ aperta: boolean; proposta: string | null }>(() => {
    const proposta = new URLSearchParams(location.search).get('unisci')
    return { aperta: !!proposta, proposta }
  })
  const [esitoUnione, setEsitoUnione] = useState<string | null>(null)
  useEffect(() => {
    const parametri = new URLSearchParams(location.search)
    if (!parametri.has('unisci')) return
    setUnione({ aperta: true, proposta: parametri.get('unisci') })
    vaiA(location.pathname, { replace: true })
  }, [location.search, location.pathname, vaiA])
  // Ricontatti e appuntamenti (465): il comando sta nell'intestazione, l'elenco nella linguetta Percorsi.
  const [versioneRicontatti, setVersioneRicontatti] = useState(0)
  const [nuovoRicontatto, setNuovoRicontatto] = useState<'ricontatto' | 'appuntamento' | null>(null)
  const ricontatti = useRicontattiScheda(id, versioneRicontatti)
  // «Modifica la scheda» (Osservazioni 4 del 23/10/2026): ogni clic fa crescere il contatore del
  // componente che deve aprirsi, il percorso «Completa la scheda» oppure i dati anagrafici. Quello dei
  // dati anagrafici torna a zero appena servito: la linguetta si smonta cambiando sezione, e al
  // ritorno non deve riaprire la modifica da sola.
  const [richiestaCompleta, setRichiestaCompleta] = useState(0)
  const [richiestaAnagrafica, setRichiestaAnagrafica] = useState(0)
  // «Riferimento per il messenger» (479): numero e collegamento della scheda, senza dati della persona.
  const riferimento = useRiferimentoMessenger(id, scheda?.numero_scheda ?? null)

  const carica = useCallback(async () => {
    try { setScheda(await api.get<SchedaPersona>(`/persone/${id}`)) }
    catch (e) {
      if (e instanceof ErroreApi && e.codice === 'SCHEDA_UNITA') {
        const { unita_in: tenuta } = e.corpo as { unita_in: string }
        vaiA(`/persone/${tenuta}`, { replace: true, state: { daSchedaUnita: true } })
        return
      }
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
    }
  }, [id, vaiA])

  useEffect(() => { void carica() }, [carica])

  // /persone/:id#consensi (dal modulo dell'intervento, quando mancano i consensi 1 e 2): la sezione
  // Consensi sta nella linguetta Anagrafica, e la pagina ci arriva da sola quando la scheda e' pronta.
  // Una volta per arrivo: dopo la registrazione dei consensi la scheda si ricarica e non deve saltare.
  const versoConsensi = location.hash === '#consensi' ? location.key : null
  const arrivoServito = useRef<string | null>(null)
  useEffect(() => {
    if (!versoConsensi || !scheda || arrivoServito.current === versoConsensi) return
    arrivoServito.current = versoConsensi
    setSezione('anagrafica')
    requestAnimationFrame(() => document.getElementById('consensi')?.scrollIntoView({ block: 'start' }))
  }, [versoConsensi, scheda])

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!scheda) return <Attesa testo="Caricamento della scheda…" />

  // Una sezione completata dalla linguetta Anagrafica esce dall'avviso dopo il ricaricamento.
  const sezioniDaCompletare = avvisiCreazione.id === id ? sezioniMancanti(avvisiCreazione.voci, scheda) : []
  const modifica = azioneModifica(scheda)
  const apriModifica = () => {
    if (modifica === 'completa') { setRichiestaCompleta((n) => n + 1); return }
    setSezione('anagrafica')
    setRichiestaAnagrafica((n) => n + 1)
  }

  return (
    <>
      <Intestazione
        titolo={scheda.etichetta}
        sottotitolo={
          // Il codice del sistema precedente resta l'identificativo con cui il personale
          // conosce la scheda e con cui sono etichettati i fascicoli cartacei: la ricerca lo
          // accettava gia', ma senza mostrarlo non si poteva verificare di essere sulla
          // scheda giusta.
          <span className="mono">
            scheda n. {scheda.numero_scheda}
            {scheda.codice_storico && ` · archivio precedente ${scheda.codice_storico}`}
            {/* Schede unite a questa (473): il loro numero e il codice dell'archivio precedente. */}
            {scheda.intestazione.schede_unite?.map((u) => ` · unita la n. ${u.numero_scheda}`
              + (u.codice_storico ? ` (archivio precedente ${u.codice_storico})` : '')).join('')}
          </span>
        }
        azioni={
          <>
            {scheda.ha_area_riservata && (
              <Etichetta tono="riservato"><IconaLucchetto /> Area riservata</Etichetta>
            )}
            {/* 460: lo stato in parole, con il perche' per chi passa sopra con il puntatore. */}
            <span title={STATI_SCHEDA[scheda.stato]?.aiuto}>
              <Etichetta tono={scheda.stato === 'completa' ? 'positivo' : scheda.stato === 'archiviata' ? 'neutro' : 'attenzione'}>
                {etichettaStatoScheda(scheda.stato)}
              </Etichetta>
            </span>
            {scheda.da_verificare && <Etichetta tono="attenzione">Da verificare</Etichetta>}
            {/* 481: persona che non puo' firmare l'informativa, registrata con le iniziali. */}
            {scheda.dati_anonimizzati && (
              <span title="Nome e cognome sono iniziali e la scheda non ha dati identificativi: niente consensi né stampa da firmare.">
                <Etichetta tono="informativo">Dati anonimizzati</Etichetta>
              </span>
            )}
            {/* Osservazioni 4: con dati obbligatori mancanti apre «Completa la scheda», altrimenti i
                dati anagrafici in modifica. Solo per chi la scheda la modifica. */}
            {modifica && (
              <Bottone variante="secondario" onClick={apriModifica}>Modifica la scheda</Bottone>
            )}
            {/* D-10: la scheda da firmare ha una pagina di stampa propria, con i testi del modulo. */}
            {scheda.puo.stampare && (
              <Link className="bottone bottone-secondario" to={`/persone/${id}/stampa`}>Stampa la scheda</Link>
            )}
            {ricontatti.dati && !scheda.mascherata && (
              <Bottone variante="secondario"
                       onClick={() => { setSezione('percorsi'); setNuovoRicontatto('ricontatto') }}>
                Fissa un ricontatto
              </Bottone>
            )}
            {/* 560: un appuntamento in sede, con stanza e durata; compare nell'agenda della sede. */}
            {ricontatti.dati && !scheda.mascherata && (
              <Bottone variante="secondario"
                       onClick={() => { setSezione('percorsi'); setNuovoRicontatto('appuntamento') }}>
                Fissa un appuntamento
              </Bottone>
            )}
            {/* Email dalla webmail, collegata alla scheda (2/11/2026): non sulle schede mascherate. */}
            {!scheda.mascherata && !scheda.dati_anonimizzati && <ScriviEmail personaId={id} email={scheda.email ?? null} />}
            {scheda.puo.unire && (
              <Bottone variante="secondario" onClick={() => setUnione({ aperta: true, proposta: null })}>
                Unisci con un’altra scheda
              </Bottone>
            )}
            {riferimento.comando}
            {scheda.puo.registrareIntervento && (
              <Link className="bottone bottone-primario" to={`/persone/${id}/intervento`}>
                <IconaPiu /> Registra intervento
              </Link>
            )}
          </>
        }
      />

      {statoUnita?.daSchedaUnita && (
        <Avviso>La scheda che hai aperto è stata unita a questa: i suoi dati sono qui.</Avviso>
      )}
      {esitoUnione && <Avviso tipo="successo">{esitoUnione}</Avviso>}
      {riferimento.avviso}
      {scheda.puo.unire && (
        <AvvisoSchedeSimili key={`${id}-${scheda.intestazione.schede_unite?.length ?? 0}`} personaId={id}
                            unisci={(altra) => setUnione({ aperta: true, proposta: altra })} />
      )}
      {unione.aperta && scheda.puo.unire && (
        <UnisciSchede scheda={{ id, numero_scheda: scheda.numero_scheda, etichetta: scheda.etichetta }}
                      proposta={unione.proposta}
                      chiudi={() => setUnione({ aperta: false, proposta: null })}
                      fatto={(testo) => { setUnione({ aperta: false, proposta: null }); setEsitoUnione(testo); void carica() }} />
      )}

      {/* D-08: sulle schede migrate senza i consensi 1 e 2 l'avviso resta, su ogni linguetta. */}
      <AvvisoSchedaMigrata consensi={scheda.consensi} />

      {/* 460: perche' la scheda e' da verificare, e come si toglie il segno. */}
      <VerificaScheda scheda={scheda} ricarica={carica} />

      {/* Indicazioni CIDIS del 17/10/2026, estese il 19/10 (460): quello che manca, obbligatorio e
          consigliato, si completa con un percorso guidato. */}
      <CompletaScheda scheda={scheda} ricarica={carica} apriSubito={apriCompleta} richiesta={richiestaCompleta}
                      vaiAllaSezione={(a) => { setSezione('anagrafica'); setAncora(a) }} />

      {sezioniDaCompletare.length > 0 && (
        <Avviso tipo="attenzione">
          <strong>La scheda è stata creata, ma alcune sezioni non sono state registrate.</strong>
          <ul className="elenco-avviso">
            {sezioniDaCompletare.map((avviso) => <li key={avviso.sezione}>{avviso.testo}</li>)}
          </ul>
          <div className="riga">
            <span>Si completano dalla linguetta Anagrafica.</span>
            <Bottone variante="secondario" dimensione="piccolo"
                     onClick={() => setAvvisiCreazione({ id, voci: [] })}>Chiudi l’avviso</Bottone>
          </div>
        </Avviso>
      )}

      <IntestazioneScheda scheda={scheda} ricarica={carica} />

      {/* Sulla scheda mascherata le mediazioni non si vedono: nominano la persona e il mediatore. */}
      <Linguette etichetta="Sezioni della scheda" attiva={sezione} cambia={setSezione}
                 voci={scheda.mascherata ? SEZIONI.filter((s) => s.valore !== 'mediazioni') : SEZIONI} />

      {sezione === 'anagrafica' && (
        <>
          <AnagraficaScheda scheda={scheda} ricarica={carica} apriAnagrafici={richiestaAnagrafica > 0}
                            aperti={() => setRichiestaAnagrafica(0)} />

          <div id="consensi">
            <ConsensiScheda scheda={scheda} ricarica={carica} />
          </div>

          {/* 492: modulo del destinatario dei progetti della persona, da stampare e caricare firmato. Dalla
              495 con il modulo CIDIS: le due firme. «Completa la scheda» porta qui. */}
          {!scheda.mascherata && (
            <div id="moduli-progetto" className="persona-ancora">
              <ModuliProgetto scheda={scheda} ricarica={carica} />
            </div>
          )}
        </>
      )}

      {sezione === 'percorsi' && ricontatti.dati && !scheda.mascherata && (
        <SezioneRicontatti personaId={id} dati={ricontatti.dati} errore={ricontatti.errore}
                           ricarica={() => setVersioneRicontatti((n) => n + 1)}
                           nuovo={nuovoRicontatto} chiudiNuovo={() => setNuovoRicontatto(null)}
                           sedePersona={scheda.sede_riferimento} />
      )}
      {sezione === 'percorsi' && (
        <SezionePercorsi personaId={id} percorsi={scheda.percorsi}
                         puoRegistrareIntervento={scheda.puo.registrareIntervento}
                         puoAprirePercorso={!!scheda.puo.aprirePercorso}
                         ricarica={carica} />
      )}

      {sezione === 'mediazioni' && !scheda.mascherata && (
        <SezioneMediazioni personaId={id} nomePersona={scheda.nome ?? scheda.etichetta}
                           sedePersona={scheda.sede_riferimento} ricaricaScheda={carica} />
      )}
      {sezione === 'documenti' && (
        <Documenti personaId={id} tipoDocumento={scheda.tipo_documento} anonimizzata={!!scheda.dati_anonimizzati} />
      )}
      {sezione === 'riservata' && <AreaRiservata personaId={id} puoScrivere={scheda.puo.scrivereRiservato} />}
      {sezione === 'cronologia' && <Cronologia personaId={id} />}
    </>
  )
}
